import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqData = [
    {
      category: 'General Questions',
      questions: [
        {
          question: 'What is GENMOZPRO?',
          answer: 'GENMOZPRO is a professional platform providing payment testing tools for developers and security researchers. We offer BIN generators, card validators, and Stripe integration testing tools, all designed for secure development and testing environments.'
        },
        {
          question: 'Is GENMOZPRO free to use?',
          answer: 'Yes! GENMOZPRO offers free access to all our core testing tools including the BIN Generator, Check Live, and Check Stripe features. We believe in supporting the developer community with reliable testing resources.'
        },
        {
          question: 'Who should use GENMOZPRO?',
          answer: 'Our platform is designed for developers, payment system integrators, security researchers, and QA teams who need to test payment integrations, validate card algorithms, and ensure PCI-DSS compliance in their applications.'
        }
      ]
    },
    {
      category: 'BIN Generator',
      questions: [
        {
          question: 'What is a BIN number?',
          answer: 'BIN (Bank Identification Number) is the first 6-8 digits of a credit/debit card number that identifies the issuing bank and card brand. Our generator uses these BINs to create valid test card numbers using the Luhn algorithm.'
        },
        {
          question: 'How many cards can I generate at once?',
          answer: 'You can generate up to 200 cards in a single request. Simply select your desired quantity (10, 20, 50, 100, or 200) and customize the month, year, and CVV parameters as needed.'
        },
        {
          question: 'Are the generated cards real?',
          answer: 'NO. All cards generated by GENMOZPRO are fictitious and for testing purposes only. They pass Luhn algorithm validation but are NOT real credit cards and cannot be used for actual transactions.'
        }
      ]
    },
    {
      category: 'Check Live & Stripe',
      questions: [
        {
          question: 'What is the difference between Check Live and Check Stripe?',
          answer: 'Check Live validates cards using our internal testing system with instant results. Check Stripe validates cards through Stripe\'s API using your own Stripe test API key, providing real-world integration testing.'
        },
        {
          question: 'How does the 3-second delay work?',
          answer: 'To simulate realistic payment processing, each card is validated successively with a 3-second interval between checks. This helps you test rate limiting, user experience, and loading states in your applications.'
        },
        {
          question: 'Is there a limit on card checks?',
          answer: 'No! Both Check Live and Check Stripe support unlimited card validation. You can test as many cards as you need for comprehensive payment system testing.'
        }
      ]
    },
    {
      category: 'Security & Compliance',
      questions: [
        {
          question: 'Is GENMOZPRO PCI-DSS compliant?',
          answer: 'Yes, our platform adheres to PCI-DSS security standards for testing environments. We never process, store, or transmit real credit card data. All generated numbers are for testing purposes only.'
        },
        {
          question: 'Do you store my test data?',
          answer: 'Test data is automatically deleted after 30 days. We only store minimal information necessary for platform operation and never retain real payment card information.'
        },
        {
          question: 'Can I use GENMOZPRO for production testing?',
          answer: 'GENMOZPRO is designed exclusively for development, sandbox, and testing environments. Never use generated cards or test data in production systems or for real transactions.'
        }
      ]
    },
    {
      category: 'API & Integration',
      questions: [
        {
          question: 'Does GENMOZPRO have an API?',
          answer: 'Yes! Our platform provides RESTful APIs for card generation and validation. Check our API documentation in the Resources section for integration guides and code examples.'
        },
        {
          question: 'What programming languages are supported?',
          answer: 'Our APIs work with any language that supports HTTP requests. We provide documentation and examples for popular languages including JavaScript, Python, PHP, Ruby, and more.'
        },
        {
          question: 'Do I need an API key?',
          answer: 'For Check Stripe functionality, you\'ll need your own Stripe test API key. Our BIN Generator and Check Live features are available without authentication for free public use.'
        }
      ]
    },
    {
      category: 'Support',
      questions: [
        {
          question: 'How can I get support?',
          answer: 'Contact us via email at support@genmozpro.com or use our Contact page. We typically respond within 24 hours during business days. For urgent issues, check our documentation first for quick solutions.'
        },
        {
          question: 'Can I report a bug or suggest a feature?',
          answer: 'Absolutely! We welcome feedback from our community. Send bug reports and feature requests to support@genmozpro.com or reach out through our Contact form. Your input helps us improve!'
        },
        {
          question: 'Is there a documentation or guide?',
          answer: 'Yes! Visit our Articles & Guides section for comprehensive tutorials, best practices, API documentation, and integration examples. We regularly update our content with the latest industry standards.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <SEO 
        title="FAQs - Frequently Asked Questions | GENMOZPRO"
        description="Find answers to common questions about GENMOZPRO payment testing tools, BIN generator, card validators, and API integration. Get help with security, compliance, and best practices."
        keywords="genmozpro faq, payment testing help, bin generator questions, card validation support, stripe integration help, pci dss compliance"
        canonical="https://genmozpro.com/faq"
      />
      <div className="min-h-screen bg-black text-white pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
              <HelpCircle className="w-4 h-4 text-blue-500" />
              <span className="text-blue-500 text-sm font-medium">Frequently Asked Questions</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              FAQs - <span className="text-blue-500">Help Center</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Find answers to common questions about our payment testing tools and services.
            </p>
          </div>

          {/* FAQ Categories */}
          <div className="space-y-8">
            {faqData.map((category, catIndex) => (
              <div key={catIndex} className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                <h2 className="text-2xl font-bold mb-6 text-blue-400">{category.category}</h2>
                <div className="space-y-4">
                  {category.questions.map((item, qIndex) => {
                    const index = `${catIndex}-${qIndex}`;
                    const isOpen = openIndex === index;
                    
                    return (
                      <div key={qIndex} className="border border-gray-700 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleFAQ(catIndex, qIndex)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-800 transition-colors"
                        >
                          <span className="font-semibold text-white pr-4">{item.question}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 text-gray-300 bg-gray-800/50">
                            <p>{item.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-12 bg-blue-500/10 border border-blue-500/20 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-3">Still have questions?</h3>
            <p className="text-gray-400 mb-6">
              Can't find what you're looking for? Our support team is here to help!
            </p>
            <a
              href="/contact"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-400 transition-all"
            >
              <span>Contact Support</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQs;
